import { DOCUMENT, Inject, Injectable, PLATFORM_ID } from "@angular/core";
import { isPlatformServer } from "@angular/common";
import { NavigationEnd, Router } from "@angular/router";
import { filter } from "rxjs";

import { environment } from "@www-src/environments/environment";

declare global {
  interface Window {
    dataLayer: any[];
  }
}

enum EGtmKeys {
  Prd = 'GTM-N4FMTMNR',
  Dev = 'GTM-N572W976'
}

@Injectable({ providedIn: 'root' })
export class GtmService {
  private previousUrl?: string;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: object,
    private router: Router,
  ) {}

  addGtm(): void {
    if (this.isLocalhost()) {
      return;
    }

    let buildEnvironment: string;

    if (isPlatformServer(this.platformId)) {
      buildEnvironment = process.env['BUILD_ENV'];
    }

    const key = (buildEnvironment === 'prd' || environment.ENVIRONMENT === 'prd') ? EGtmKeys.Prd : EGtmKeys.Dev;

    this.addGtmToHead(key);
    this.addGtmToBody(key);
    this.listenToPageViewEvent();
  }

  private listenToPageViewEvent(): void {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd)
      )
      .subscribe(() => this.dataLayerPageViewEvent())
  }

  private dataLayerPageViewEvent(): void {
    if (isPlatformServer(this.platformId)) {
      return;
    }

    const pageUrl = window.location.href;

    window.dataLayer.push({
      event: 'virtual_page_view',
      pageUrl,
      pageTitle: this.document.title,
      previousUrl: this.previousUrl
    });

    this.previousUrl = pageUrl;
  }

  private isLocalhost(): boolean {
    const url = this.document.location.href;

    return url.includes('localhost:') || url.includes('codebuild.dev:');
  }

  private addGtmToHead(key: string): void  {
    this.initDataLayer();
    this.insertGtmHeadScript(key);
  }

  private initDataLayer(): void {
    if (isPlatformServer(this.platformId)) {
      return;
    }

    window.dataLayer = window.dataLayer || [];

    window.dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    });
  }

  private insertGtmHeadScript(key: string): void {
    const script = this.document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${key}`;

    this.document.head.appendChild(script);
  }

  private addGtmToBody(key: string): void {
    this.document.addEventListener('DOMContentLoaded', () => {
      const iframe = this.document.createElement('iframe');
      const noscript = this.document.createElement('noscript');

      iframe.src = `https://www.googletagmanager.com/ns.html?id=${key}`;
      iframe.height = '0';
      iframe.width = '0';
      iframe.style.display = 'none';
      iframe.style.visibility = 'hidden';
      
      noscript.appendChild(iframe);

      this.document.body.insertBefore(noscript, this.document.body.firstChild);
    })
  }
}
